import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, TrendingUp, Check, Sparkles, ExternalLink } from "lucide-react";
import DemoLayout from "@/components/DemoLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOG_LINES_TEMPLATE = [
  "Parsing job description",
  "Extracting must-have keywords (12 found)",
  "Pulling {firstName}'s raw resume from {pronoun} profile",
  "Running ATS pass 1: as-is",
  "Score: {raw} / 100 (filtered out)",
  "Composing Sisyphean-tailored resume",
  "Mirroring 11 must-have keywords verbatim",
  "Re-ordering portfolio to lead with relevant work",
  "Running ATS pass 2: optimized",
  "Score: {opt} / 100 ({rec})",
  "Compiling delta + breakdown...",
];

const FEMALE_FIRST_NAMES = new Set(["maya"]);
const MALE_FIRST_NAMES = new Set([]);

function buildLogLines({ firstName, personaId, rawScore, optScore, optRec }) {
  const pronoun = FEMALE_FIRST_NAMES.has(personaId) ? "her" : MALE_FIRST_NAMES.has(personaId) ? "his" : "their";
  return LOG_LINES_TEMPLATE.map((s) =>
    s.replace("{firstName}", firstName)
     .replace("{pronoun}", pronoun)
     .replace("{raw}", rawScore)
     .replace("{opt}", optScore)
     .replace("{rec}", optRec),
  );
}

/**
 * Showcase page: hand-crafted ATS comparison rendered with a scripted
 * loading sequence that always finishes on a guaranteed-large score jump.
 */
export default function DemoAtsCompare() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const personaId = params.get("persona");
  const navigate = useNavigate();

  const [demo, setDemo] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [logIndex, setLogIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // Initial fetch: data + ats comparison kicked off in parallel
  useEffect(() => {
    let alive = true;
    const fetchAll = async () => {
      try {
        const [d, r] = await Promise.all([
          axios.get(`${API}/demo/data`),
          axios.post(`${API}/demo/ats-compare`, { persona_id: personaId, job_id: jobId }),
        ]);
        if (!alive) return;
        setDemo(d.data);
        setResult(r.data);
      } catch {
        if (alive) navigate(`/demo/dashboard?persona=${personaId || ""}`);
      } finally {
        if (alive) setBootstrapping(false);
      }
    };
    fetchAll();
    return () => { alive = false; };
  }, [personaId, jobId, navigate]);

  const persona = useMemo(() => demo?.personas.find((p) => p.id === personaId), [demo, personaId]);
  const job = useMemo(() => demo?.jobs.find((j) => j.id === jobId), [demo, jobId]);

  const logLines = useMemo(() => {
    if (!persona || !result) return [];
    return buildLogLines({
      firstName: persona.name.split(" ")[0],
      personaId: persona.id,
      rawScore: result.raw.eval.overall_score,
      optScore: result.optimized.eval.overall_score,
      optRec: result.optimized.eval.hire_recommendation,
    });
  }, [persona, result]);

  // Once we're not bootstrapping anymore, run the scripted log sequence
  useEffect(() => {
    if (bootstrapping || logLines.length === 0) return undefined;
    if (logIndex >= logLines.length) {
      const t = setTimeout(() => setShowResult(true), 350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setLogIndex((i) => i + 1), 330);
    return () => clearTimeout(t);
  }, [bootstrapping, logIndex, logLines.length]);

  if (bootstrapping || !persona || !job || !result) {
    return (
      <DemoLayout>
        <div className="py-32 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading
        </div>
      </DemoLayout>
    );
  }

  return (
    <DemoLayout persona={persona}>
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-10 md:pt-14 pb-24">
        {/* Job header */}
        <Link
          to={`/demo/dashboard?persona=${personaId}`}
          data-testid="demo-back-to-feed"
          className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 mb-5"
        >
          ← Back to feed
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div className="min-w-0 flex-1">
            <div className="terminal-label text-primary mb-2">[ ATS · A/B COMPARISON ]</div>
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase font-bold tracking-tight leading-tight">{job.title}</h1>
            <div className="text-sm text-muted-foreground mt-2">{job.company} · {job.location}</div>
            {job.salary && <div className="text-xs text-accent-green font-mono mt-1">{job.salary}</div>}
          </div>
        </div>

        {!showResult ? (
          <AnalyzingPanel logLines={logLines} logIndex={logIndex} candidateName={persona.name} jobTitle={job.title} />
        ) : (
          <ResultPanel result={result} />
        )}
      </div>
    </DemoLayout>
  );
}

// ---------------------------------------------------------------------------
// Scripted "Claude is composing..." style loading panel
// ---------------------------------------------------------------------------
function AnalyzingPanel({ logLines, logIndex, candidateName, jobTitle }) {
  const visible = logLines.slice(0, logIndex + 1);
  return (
    <div className="panel p-6 sm:p-10" data-testid="demo-analyzing">
      <div className="terminal-label text-primary mb-3 blink-caret">
        SISYPHEAN ANALYZING: {candidateName.toUpperCase()} → {jobTitle.toUpperCase()}
      </div>
      <p className="text-xs text-muted-foreground mb-6">Running both arms in parallel: raw resume + Sisyphean-tailored resume against the job's ATS.</p>
      <ul className="space-y-1.5 font-mono text-[12px] leading-relaxed" data-testid="demo-analyzing-log">
        {visible.map((line, i) => {
          const isLatest = i === visible.length - 1 && logIndex < logLines.length;
          return (
            <li key={i} className={isLatest ? "text-foreground" : "text-foreground/55"}>
              <span className="text-primary mr-2">{isLatest ? "▶" : "✓"}</span>
              {line}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result panel (reveals the delta + both arms side-by-side)
// ---------------------------------------------------------------------------
function ResultPanel({ result }) {
  return (
    <div data-testid="demo-ats-result" className="animate-in fade-in duration-500">
      <DeltaBanner delta={result.delta} candidateName={result.candidate_name} jobTitle={result.job.title} company={result.job.company} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
        <Arm label={result.raw.label} arm={result.raw} testId="demo-ats-arm-raw" tone="muted" />
        <Arm label={result.optimized.label} arm={result.optimized} testId="demo-ats-arm-optimized" tone="primary" />
      </div>

      {/* Conversion CTA */}
      <div className="mt-10 p-6 sm:p-8 brut-tile" style={{ borderColor: "hsl(var(--primary) / 0.5)" }}>
        <div className="flex items-start gap-4 flex-wrap">
          <Sparkles className="w-5 h-5 text-primary mt-1" strokeWidth={1.5} />
          <div className="min-w-0 flex-1">
            <h3 className="serif-mod text-2xl sm:text-3xl tracking-tight leading-tight mb-2" style={{ fontWeight: 500 }}>
              This is what Sisyphean does for every job in your feed.
            </h3>
            <p className="text-sm text-foreground/75 leading-relaxed max-w-2xl mb-5">
              Upload your resume once. Get 30-80 ranked jobs every day, each with a tailored resume, cover letter, and portfolio page already written. 7-day free trial, then $19/mo.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/auth?mode=signup" data-testid="demo-result-signup" className="btn-primary inline-flex items-center gap-2">
                Sign up free <ArrowRight className="w-3 h-3" />
              </Link>
              <Link to="/" data-testid="demo-result-back" className="btn-secondary text-[10px]">
                Back to landing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeltaBanner({ delta, candidateName, jobTitle, company }) {
  return (
    <div className="panel p-5 sm:p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="demo-delta-banner">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1.5">
          {candidateName} → {jobTitle} · {company}
        </div>
        <div className="flex items-center gap-2 text-accent-green">
          <TrendingUp className="w-5 h-5" strokeWidth={2} />
          <span className="serif-mod text-4xl sm:text-5xl tracking-tight" style={{ fontWeight: 500 }} data-testid="demo-delta-label">
            +{delta} pts with Sisyphean
          </span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
        Positive means Sisyphean's tailored resume moved {candidateName.split(" ")[0]} further up the ATS ranking against this specific job.
      </p>
    </div>
  );
}

function Arm({ label, arm, testId, tone }) {
  const { eval: e } = arm;
  const recStyle = recommendationStyle(e.hire_recommendation);
  const armBorder = tone === "primary" ? "border-primary/40" : "border-foreground/15";
  return (
    <div className={`panel p-5 ${armBorder}`} data-testid={testId} style={tone === "primary" ? { backgroundColor: "hsl(var(--primary) / 0.04)" } : undefined}>
      <div className={`terminal-label mb-3 ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`}>{label}</div>

      <div className="flex items-center gap-4 mb-5">
        <div className="serif-mod text-6xl tracking-[-0.04em] leading-none" style={{ fontWeight: 500 }} data-testid={`${testId}-score`}>
          {e.overall_score}
        </div>
        <div className="flex-1">
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 border ${recStyle.border} ${recStyle.bg} ${recStyle.text} text-[10px] uppercase tracking-[0.18em] font-mono mb-2`}>
            <span>{recStyle.icon}</span> {e.hire_recommendation}
          </div>
          <div className={`text-[10px] uppercase tracking-[0.2em] font-mono ${e.ats_pass ? "text-accent-green" : "text-destructive"}`}>
            {e.ats_pass ? "✓ Passes filter" : "✗ Filtered out"} · {e.hire_confidence}% confidence
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground/80 leading-relaxed italic mb-5 pl-3 border-l-2 border-foreground/20">
        &quot;{e.recruiter_summary}&quot;
      </p>

      <div className="space-y-2 mb-5">
        {Object.entries(e.score_breakdown).map(([key, val]) => (
          <div key={key}>
            <div className="flex items-center justify-between text-[11px] font-mono mb-1">
              <span className="text-foreground/80 uppercase tracking-wide">{key.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground tabular-nums">{val.score} · ×{val.weight}%</span>
            </div>
            <div className="h-1 bg-surface relative">
              <div className="absolute inset-y-0 left-0" style={{ width: `${val.score}%`, backgroundColor: barColor(val.score) }} />
            </div>
          </div>
        ))}
      </div>

      {e.knockout_flags?.length > 0 && (
        <div className="p-3 border border-destructive/40 bg-destructive/5 text-[11px] text-destructive mb-4 font-mono">
          <div className="uppercase tracking-[0.15em] mb-1">⚠ Knockout disqualifiers</div>
          {e.knockout_flags.map((f, i) => <div key={i}>· {f}</div>)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {e.strengths?.length > 0 && (
          <div>
            <div className="terminal-label text-accent-green mb-2">✓ Strengths</div>
            <ul className="text-[11px] text-foreground/80 leading-relaxed space-y-1">
              {e.strengths.map((s, i) => <li key={i}>· {s}</li>)}
            </ul>
          </div>
        )}
        {e.gaps?.length > 0 && (
          <div>
            <div className="terminal-label text-accent-amber mb-2">✗ Gaps</div>
            <ul className="text-[11px] text-foreground/80 leading-relaxed space-y-1">
              {e.gaps.map((g, i) => <li key={i}>· {g}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function barColor(score) {
  if (score >= 80) return "hsl(var(--accent-green))";
  if (score >= 60) return "hsl(var(--accent-amber, 38 100% 50%))";
  return "hsl(var(--destructive))";
}

function recommendationStyle(rec) {
  const map = {
    "Strong Hire":  { bg: "bg-accent-green/15",  border: "border-accent-green/60",  text: "text-accent-green",  icon: "✦" },
    "Lean Hire":    { bg: "bg-accent-green/10",  border: "border-accent-green/40",  text: "text-accent-green",  icon: "◆" },
    "On the Fence": { bg: "bg-accent-amber/15",  border: "border-accent-amber/60",  text: "text-accent-amber",  icon: "◇" },
    "Lean No Hire": { bg: "bg-destructive/10",   border: "border-destructive/40",   text: "text-destructive",   icon: "▼" },
    "No Hire":      { bg: "bg-destructive/15",   border: "border-destructive/60",   text: "text-destructive",   icon: "✕" },
  };
  return map[rec] || map["On the Fence"];
}
